import React, {useState} from "react";
import useTranslate from "../../core/languages/hooks/useTranslate";
import {formatNumber} from "../../common/utils/number";
import {ArrowPathIcon, CheckCircleIcon, ChevronDownIcon} from "@heroicons/react/24/outline";
import PaymentRow from "../../payment/components/PaymentRow";
import Payment from "../../payment/types/Payment";
import Transfer from "../../transfers/types/Transfer";
import {TransferCommonDescription} from "../../transfers/types/TransferCommonDescription";
import useTransferPaymentsGet from "../hooks/useTransferPaymentsGet";
import {TransferStatusEnum} from "../../transfers/enums/TransferStatusEnum";
import {ItemRow, TotalRow} from "../../payment/components/SummaryComponents";
import useCurrencyGet from "../../payment/hooks/useCurrencyGet";

interface TransferPriceSummaryProps {
  transferInfo: Transfer;
  parentTransfer: TransferCommonDescription;
}

const TransferPriceSummary: React.FC<TransferPriceSummaryProps> = ({transferInfo, parentTransfer}) => {
  const t = useTranslate();
  const transferInternalId = transferInfo.uuid || transferInfo.id;
  const paymentsQuery = useTransferPaymentsGet(transferInternalId);
  const transferPayments: Payment[] = paymentsQuery.data || [];
  const [paymentsOpen, setPaymentsOpen] = useState<boolean>(false);
  const [refundsOpen, setRefundsOpen] = useState<boolean>(false);

  const payments = transferPayments.filter((p) => p.type === 1);
  const refunds = transferPayments.filter((p) => p.type !== 1);
  
  // En transfers: primary es originalAmount (moneda original del pago)
  const totalPayments = payments.reduce((sum, p) => sum + Number(p.originalAmount || p.amount), 0);
  const totalRefunds = refunds.reduce((sum, p) => sum + Number(p.originalAmount || p.amount), 0);
  
  // Obtener moneda de transfer (parentTransfer.currency puede ser IRI o objeto)
  const currencyValue = typeof parentTransfer.currency === 'string' ? parentTransfer.currency : (parentTransfer.currency as any)?.iri || null;
  const {data: primaryCurrencyData} = useCurrencyGet(currencyValue);
  const transferCurrencySymbol = primaryCurrencyData?.symbol || (typeof parentTransfer.currency === 'object' && (parentTransfer.currency as any)?.symbol);
  
  // Encontrar pagos/reembolsos con conversión de moneda
  const paymentsConConversion = payments.find((p) => p.originalCurrency && p.currency && p.originalCurrency !== p.currency);
  const refundsConConversion = refunds.find((p) => p.originalCurrency && p.currency && p.originalCurrency !== p.currency);
  
  // Obtener símbolo de moneda original (para primary en transfers cuando hay conversión)
  const {data: paymentsOriginalCurrencyData} = useCurrencyGet(paymentsConConversion?.originalCurrency);
  const {data: refundsOriginalCurrencyData} = useCurrencyGet(refundsConConversion?.originalCurrency);
  const paymentsOriginalCurrencySymbol = paymentsOriginalCurrencyData?.symbol;
  const refundsOriginalCurrencySymbol = refundsOriginalCurrencyData?.symbol;
  
  // Usar moneda original si hay conversión, si no, usar moneda del transfer
  const paymentsPrimarySymbol = paymentsOriginalCurrencySymbol || transferCurrencySymbol;
  const refundsPrimarySymbol = refundsOriginalCurrencySymbol || transferCurrencySymbol;
  
  // Total en moneda de transfer (amount cuando hay conversión)
  const totalPaymentsOther = payments.reduce((sum, p) => {
    const hasSecondaryCurrency = Boolean(p.originalCurrency && p.currency && p.originalCurrency !== p.currency);
    return sum + (hasSecondaryCurrency ? Number(p.amount || 0) : 0);
  }, 0);
  const totalRefundsOther = refunds.reduce((sum, p) => {
    const hasSecondaryCurrency = Boolean(p.originalCurrency && p.currency && p.originalCurrency !== p.currency);
    return sum + (hasSecondaryCurrency ? Number(p.amount || 0) : 0);
  }, 0);

  if (transferInfo.status === TransferStatusEnum.CANCELLED) return null;
  
  const renderAmountPair = (primary: number, primarySymbol: string, secondary?: number, secondarySymbol?: string, primaryClassName = "text-lg font-bold", prefix = "") => (
    <div className="flex flex-col items-end">
      <span className={primaryClassName}>{prefix}{formatNumber(primary)} {primarySymbol}</span>
      {secondary !== undefined && secondary > 0 && secondarySymbol && (
        <span className="text-[11px] leading-tight text-neutral-500 dark:text-neutral-400">
          {formatNumber(secondary)} {secondarySymbol}
        </span>
      )}
    </div>
  );

  return (
    <div className="max-w-full space-y-4">
      <h3 className="text-3xl lg:text-2xl font-semibold mb-2">{t('bookOk.summaryTitle')}</h3>
      <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-6">{t('bookOk.summarySubtitle')}</p>
      {/* ── Desglose: habitaciones + impuestos + total ── */}
      <div
        className="bg-white dark:bg-neutral-900 border border-neutral-100 dark:border-neutral-800 rounded-2xl divide-y divide-neutral-100 dark:divide-neutral-800 overflow-hidden">
        {/* Transfer */}
        <div className="px-4">
          <ItemRow
            icon={
              <i className="las la-car-side text-2xl text-indigo-500"></i>
            }
            iconBg="bg-indigo-50 dark:bg-indigo-900/20"
            title={t('transferOk.fields.totalTransfer')}
            subtitle={t('transferOk.fields.totalTransferSubtitle')}
            amount={
              <span className="text-base font-semibold text-neutral-900 dark:text-neutral-100">
                {formatNumber(parentTransfer.lovePrice)} {transferCurrencySymbol}
              </span>
            }
          />
        </div>

        {/* Total de la reserva */}
        <TotalRow
          label={t('transferOk.fields.totalTransfer')}
          amount={<>{formatNumber(parentTransfer.lovePrice)} {transferCurrencySymbol}</>}
        />
      </div>

      {/* ── Pagos y reembolsos (solo si hay movimientos) ── */}
      {(transferInfo.totalPaid > 0 || refunds.length > 0) && (
        <div
          className="bg-white dark:bg-neutral-900 border border-neutral-100 dark:border-neutral-800 rounded-2xl divide-y divide-neutral-100 dark:divide-neutral-800 overflow-hidden">

          {/* Pagos */}
          {transferInfo.totalPaid > 0 && (
            <div className="px-4">
              <ItemRow
                icon={<CheckCircleIcon className="w-6 h-6 text-green-500"/>}
                iconBg="bg-green-50 dark:bg-green-900/20"
                title={t('bookOk.payments') || 'Pagos'}
                subtitle={t('bookOk.paymentsSubtitle')}
                amount={renderAmountPair(
                  paymentsQuery.isLoading ? transferInfo.totalPaid : totalPayments,
                  paymentsPrimarySymbol,
                  paymentsQuery.isLoading ? undefined : totalPaymentsOther,
                  transferCurrencySymbol,
                  "text-lg font-bold text-green-600"
                )}
                onClick={() => setPaymentsOpen((s) => !s)}
                endElement={<ChevronDownIcon
                  className={`w-5 h-5 text-neutral-400 transition-transform duration-200 ${paymentsOpen ? 'rotate-180' : ''}`}/>}
              />
              {paymentsOpen && (
                <div className="pb-3">
                  {paymentsQuery.isLoading &&
                    <div className="text-xs text-neutral-500 py-2">{t('common.actions.loading') || 'Loading...'}</div>}
                  {!paymentsQuery.isLoading && payments.length === 0 &&
                    <div className="text-xs text-neutral-500 py-2">{t('bookOk.noPayments') || 'Sin pagos'}</div>}
                  {payments.length > 0 && (
                    <div className="rounded-xl border border-neutral-100 dark:border-neutral-800 overflow-hidden">
                      {payments.map((p) => (
                        <PaymentRow key={p.id} payment={p} currencySymbol={transferCurrencySymbol}/>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Reembolsos */}
          {refunds.length > 0 && (
            <div className="px-4">
              <ItemRow
                icon={<ArrowPathIcon className="w-6 h-6 text-red-500"/>}
                iconBg="bg-red-50 dark:bg-red-900/20"
                title={t('bookOk.refunds') || 'Reembolsos'}
                subtitle={t('bookOk.refundsSubtitle') || 'Importe total reembolsado'}
                amount={renderAmountPair(
                  totalRefunds,
                  refundsPrimarySymbol,
                  totalRefundsOther,
                  transferCurrencySymbol,
                  "text-lg font-bold text-red-500",
                  "−"
                )}
                onClick={() => setRefundsOpen((s) => !s)}
                endElement={<ChevronDownIcon
                  className={`w-5 h-5 text-neutral-400 transition-transform duration-200 ${refundsOpen ? 'rotate-180' : ''}`}/>}
              />
              {refundsOpen && (
                <div className="pb-3">
                  <div className="rounded-xl border border-neutral-100 dark:border-neutral-800 overflow-hidden">
                    {refunds.map((p) => (
                      <PaymentRow key={p.id} payment={p} currencySymbol={transferCurrencySymbol}/>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TransferPriceSummary;
